import { Context } from '@opentelemetry/api';
import React, { ComponentType } from 'react';
import { SpanRegistrar } from '@trp-ta-nitro/otel-browser-js';
import { otelContext } from './OtelContextProvider';
import { registrarContext } from './SpanRegistrarProvider';

// WithOtelContext
// ===============

export interface WithOtelContextProps {
  otelContext: Context;
}

/**
 * Take a component arg that requires an `otelContext` property and returns a new component that is
 * automatically given 'otelContext' from React context. The new component will no longer require an
 * `otelContext` property.
 *
 * This must be nested beneath an <OtelContextProvider /> as this uses its state.
 *
 * @param Component
 */
export const withOtelContext = <P extends object>(
  Component: ComponentType<P & WithOtelContextProps>,
) =>
  class WithOtelContext extends React.Component<Omit<P, 'otelContext'>> {
    render() {
      const props = this.props as P;
      return (
        <otelContext.Consumer>
          {(context) => {
            return <Component otelContext={context} {...props} />;
          }}
        </otelContext.Consumer>
      );
    }
  };

// WithSpanRegistrar
// =================

export interface WithSpanRegistrarProps {
  spanRegistrar?: SpanRegistrar;
}

/**
 * Take a component arg that requires an `spanRegistrar` property and returns a new component that
 * is automatically given 'spanRegistrar' from React context. The new component will no longer
 * require a `spanRegistrar` property.
 *
 * This must be nested beneath an <SpanRegistrarContextProvider /> as this uses its state.
 *
 * @param Component
 */
export const withSpanRegistrar = <P extends object>(
  Component: ComponentType<P & WithSpanRegistrarProps>,
) =>
  class WithSpanRegistrar extends React.Component<Omit<P, 'spanRegistrar'>> {
    render() {
      const props = this.props as P;
      return (
        <registrarContext.Consumer>
          {(registrar) => {
            return <Component spanRegistrar={registrar} {...props} />;
          }}
        </registrarContext.Consumer>
      );
    }
  };
